import { errorDialog, PjElement } from "@/core";
import { html, transitionsCompleted } from "@/functions";
import { APP_ROUTES, Route } from "../pages/APP_ROUTES";
import { customElement } from "./custom-element-decorator";

import "./pj-router-element.css";

@customElement("pj-router")
export class PjRouterElement extends PjElement {
  private readonly _main = document.createElement("main");
  private readonly _internals = this.attachInternals();

  protected async initialize(first: boolean): Promise<void> {
    if (first) {
      this.replaceChildren(this._main);
    }

    await this.renderRoute();
  }

  /** Render the page associated with the current route. */
  async renderRoute(): Promise<void> {
    const href = location.href;
    const pathname = location.pathname.toLowerCase();

    const match = APP_ROUTES.find((r) => r.pathName === pathname);

    if (!match) {
      this._main.innerHTML = html`
        <p>
          The page requested at path <kbd>${pathname}</kbd> does not exist.
          <a href="/">Return home</a>.
        </p>
      `;
      console.error("Unable to find route for", href);
      errorDialog("The requested page does not exist. Please return to the home page.");
      return;
    }

    try {
      // Start fading but concurrently begin to process the navigation
      // (load resources, etc). The fadeout is awaited before changing page content.
      let fadeOut = this._fade("out");

      let component: Route["loadComponent"] extends () => Promise<infer T> ? T : never;
      try {
        component = await match.loadComponent();
      } catch (e) {
        throw new TypeError(
          "Failed to lazy-load resources for the requested page. Ensure you have a working internet connection and try again.",
          { cause: e },
        );
      }

      document.title =
        match.title === "Prayer Journal" ? match.title : match.title + " | Prayer Journal";

      // Ensure the content is hidden to avoid jarring transitions
      await fadeOut;

      const h1 = document.getElementsByTagName("h1")?.[0];
      if (h1) {
        h1.textContent = match.title;
      }
      this._main.replaceChildren(component);

      // raise the event before fading in so that related transitions will not be visible
      const event = new CustomEvent("spa.navigate", {
        detail: { url: href },
        cancelable: true,
        bubbles: true,
      });
      if (!this.dispatchEvent(event)) {
        throw new Error("The spa.navigate event was cancelled, which aborts soft navigation.");
      }

      await component.ready;
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") {
        console.warn("Navigation to", href, "interrupted by abort.");
      } else {
        console.error("Failed to navigate to ", href, e);
        errorDialog(e);
      }
    } finally {
      await this._fade("in");
    }
  }

  private async _fade(direction: "in" | "out"): Promise<void> {
    if (direction === "out") {
      this._internals.states.add("navigating");
    } else {
      this._internals.states.delete("navigating");
    }
    await transitionsCompleted(this);
  }
}
